import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import {
  BadgeDollarSign,
  Building,
  ChevronDown,
  Search,
  User,
  X,
} from "lucide-react";

import { search } from "../../api/ProjectApi";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/shadcn/popover";
import { ContentTypes } from "../../constants/action-types";
import { cn } from "../../lib/utils";
import type { CrmChatRecordContext, CrmRecordType } from "../types/chat.types";

const RECORD_SEARCH_TYPES = [ContentTypes.SALE, ContentTypes.COMPANY, ContentTypes.CONTACT] as const;

type SearchContentType = (typeof RECORD_SEARCH_TYPES)[number];

const TYPE_LABELS: Record<SearchContentType, string> = {
  [ContentTypes.SALE]: "Deal",
  [ContentTypes.COMPANY]: "Account",
  [ContentTypes.CONTACT]: "Contact",
};

const RECORD_ICONS: Record<CrmRecordType, React.ReactNode> = {
  COMPANY: <Building className="h-3.5 w-3.5" />,
  CONTACT: <User className="h-3.5 w-3.5" />,
  SALE: <BadgeDollarSign className="h-3.5 w-3.5" />,
};

const RECORD_LABELS: Record<CrmRecordType, string> = {
  COMPANY: "Company",
  CONTACT: "Contact",
  SALE: "Deal",
};

function toRecordType(type: string): CrmRecordType {
  if (type === ContentTypes.COMPANY) return "COMPANY";
  if (type === ContentTypes.CONTACT) return "CONTACT";
  return "SALE";
}

function buildSubtitle(item: { getIn: (path: string[]) => unknown }): string | undefined {
  const stage = item.getIn(["data", "stage", "value", "label"]) ?? item.getIn(["data", "stage", "value"]);
  const email = item.getIn(["data", "email", "value"]);
  if (stage) return String(stage);
  if (email) return String(email);
  return undefined;
}

export interface CrmChatRecordPickerProps {
  workspaceId: string;
  value?: CrmChatRecordContext | null;
  onChange: (record: CrmChatRecordContext | null) => void;
  disabled?: boolean;
}

interface SearchHit {
  id: string;
  label: string;
  type: SearchContentType;
  subtitle?: string;
}

const CrmChatRecordPicker: React.FC<CrmChatRecordPickerProps> = ({
  workspaceId,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchType, setSearchType] = useState<SearchContentType>(ContentTypes.SALE);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const runSearch = useCallback(
    async (q: string, type: SearchContentType) => {
      setLoading(true);
      try {
        const filters =
          q.trim().length > 0
            ? {
                name: {
                  operator: "regex",
                  values: [q.trim()],
                },
              }
            : {};
        const data = await search({
          projectId: workspaceId,
          pageSize: 8,
          types: [type],
          filters,
        });
        const content = fromJS(data?.pageResult?.content ?? []);
        const hits: SearchHit[] = content
          .map((item: { get: (k: string) => unknown; getIn: (path: string[]) => unknown }) => ({
            id: item.get("id") as string,
            label: (item.getIn(["data", "name", "value"]) as string) || (item.get("id") as string),
            type,
            subtitle: buildSubtitle(item),
          }))
          .toJS();
        setResults(hits);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [workspaceId]
  );

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => runSearch(query, searchType), 200);
    return () => window.clearTimeout(timer);
  }, [open, query, searchType, runSearch]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else {
      setQuery("");
    }
  }, [open]);

  const selectHit = (hit: SearchHit) => {
    onChange({
      recordId: hit.id,
      recordType: toRecordType(hit.type),
      displayName: hit.label,
      subtitle: hit.subtitle,
    });
    setOpen(false);
  };

  const trigger = value ? (
    <div className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 py-1 pl-2 pr-1 text-xs text-gray-800">
      <button
        type="button"
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-full text-left disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="text-gray-500" aria-hidden="true">
          {RECORD_ICONS[value.recordType]}
        </span>
        <span className="font-medium">{value.displayName}</span>
        {value.subtitle ? <span className="text-gray-500">· {value.subtitle}</span> : null}
        <span className="ml-0.5 inline-flex items-center gap-0.5 px-1 text-[11px] text-gray-600">
          {t("Change")}
          <ChevronDown className="h-3 w-3" />
        </span>
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onChange(null);
        }}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={t("Remove record")}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  ) : (
    <button
      type="button"
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Building className="h-3.5 w-3.5" />
      {t("Choose a CRM record")}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger}
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[200] w-80 p-0" sideOffset={6}>
        <div className="border-b border-gray-100 p-2">
          <div className="mb-2 flex gap-1">
            {RECORD_SEARCH_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setSearchType(type)}
                className={cn(
                  "rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
                  searchType === type
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                )}
              >
                {t(TYPE_LABELS[type])}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-md bg-gray-50 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-gray-400" aria-hidden />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("Search by name…") as string}
              className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            />
          </div>
        </div>
        <ul className="max-h-52 overflow-y-auto py-1" role="listbox">
          {loading ? (
            <li className="px-3 py-3 text-center text-xs text-gray-500">{t("Searching…")}</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-3 text-center text-xs text-gray-500">
              {query.trim() ? t("No matches") : t("Type to search or pick a record type above")}
            </li>
          ) : (
            results.map((hit) => (
              <li key={hit.id}>
                <button
                  type="button"
                  role="option"
                  onClick={() => selectHit(hit)}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <span className="mt-0.5 text-gray-500">{RECORD_ICONS[toRecordType(hit.type)]}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-900">{hit.label}</span>
                    <span className="block truncate text-xs text-gray-500">
                      {t(TYPE_LABELS[hit.type])}
                      {hit.subtitle ? ` · ${hit.subtitle}` : ""}
                    </span>
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
        <p className="border-t border-gray-100 px-3 py-2 text-[10px] text-gray-500">
          {t("Record-scoped prompts need a deal, account, or contact.")}
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default CrmChatRecordPicker;
